import './select.scss';

import {
  ChangeEvent,
  ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

import { ExpandMoreRoundedIcon } from '../Icons/ExpandMoreRoundedIcon';
import { toErrorMessage } from '../Input/functions/toErrorMessage';
import { SelectProps } from './types/SelectProps';

export const Select = ({
  error = false,
  errorTestId,
  inputTestId,
  label,
  options,
  ...props
}: SelectProps): ReactNode => {
  const generatedId = useId();
  const selectId = props.id ?? generatedId;

  const selectRef = useRef<HTMLSelectElement | null>(null);
  // The ref that came in with the props, held in a box so that the callback
  // below can stay stable - an unstable ref callback re-runs on every render.
  const forwardedRef = useRef(props.ref);
  forwardedRef.current = props.ref;

  // Drives the label's resting position. Read off the element rather than from
  // a prop, because react-hook-form writes the value straight to the DOM.
  const [hasValue, setHasValue] = useState(false);

  const hasError = Boolean(error);
  const errorMessage = toErrorMessage(error);

  const fieldClassNames = [
    'select-field',
    hasError && 'select-field-error',
    !hasValue && 'select-field-empty',
  ]
    .filter(Boolean)
    .join(' ');

  const setSelectRef = useCallback(
    (element: HTMLSelectElement | null): void => {
      selectRef.current = element;

      const { current } = forwardedRef;

      if (typeof current === 'function') {
        current(element);
      } else if (current) {
        current.current = element;
      }
    },
    [],
  );

  useEffect((): void => {
    // On mount, and again whenever the options change - a cascading select is
    // reset by its parent at the same time as its options reload.
    setHasValue(Boolean(selectRef.current?.value));
  }, [options]);

  const handleChange = (event: ChangeEvent<HTMLSelectElement>): void => {
    setHasValue(Boolean(event.target.value));

    if (props.onChange) {
      props.onChange(event);
    }
  };

  return (
    <div className={fieldClassNames}>
      <select
        aria-invalid={hasError}
        {...props}
        className="select"
        data-testid={inputTestId}
        id={selectId}
        ref={setSelectRef}
        onChange={handleChange}
      >
        {/* Empty, hidden and disabled: it exists so that `value=''` resolves to
            a real option, but it must not add a blank row to the list or print
            the label a second time underneath the floating one. */}
        <option value="" disabled hidden />

        {options.map(
          ({ name: optionName, value: optionValue }): ReactNode => (
            <option key={String(optionValue)} value={optionValue}>
              {optionName}
            </option>
          ),
        )}
      </select>

      <label className="select-label" htmlFor={selectId}>
        {label}
      </label>

      <span className="select-icon">
        <ExpandMoreRoundedIcon />
      </span>

      {errorMessage && (
        <span className="field-error" data-testid={errorTestId}>
          {errorMessage}
        </span>
      )}
    </div>
  );
};
